import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { PsychoeducationArticlePreview } from '@lighthouse/shared';
import { api } from '../services/api';
import { LearnArticleCard, LearnArticleCardSkeleton } from './learn/LearnArticleCard';
import { RevealOnScroll } from './landing/RevealOnScroll';
import { QueryError } from './QueryError';

export function LearnGrowSection() {
  const [expandedSlug, setExpandedSlug] = useState<string | null>(null);

  const {
    data: articles = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['learn-featured'],
    queryFn: () =>
      api.get<PsychoeducationArticlePreview[]>('/content/featured?language=en&limit=3'),
    staleTime: 60_000,
  });

  if (isError) {
    return (
      <section className="landing-section-alt">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <QueryError message="Could not load articles" onRetry={() => void refetch()} />
        </div>
      </section>
    );
  }

  if (!isLoading && articles.length === 0) {
    return null;
  }

  return (
    <section className="landing-section-alt" aria-labelledby="learn-grow-heading">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <RevealOnScroll>
          <div className="learn-section-header">
            <div>
              <p className="landing-section-kicker">Learn &amp; grow</p>
              <h2 id="learn-grow-heading" className="landing-section-title">
                Articles for your mental wellness
              </h2>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-gray-600 sm:text-base">
                Evidence-based psychoeducation on anxiety, mood, and knowing when to seek support —
                written for everyday life in Nigeria.
              </p>
            </div>
            <Link to="/learn" className="learn-section-link">
              Browse all articles
              <span aria-hidden>→</span>
            </Link>
          </div>
        </RevealOnScroll>

        {isLoading ? (
          <div className="learn-articles-grid mt-10">
            {[0, 1, 2].map((i) => (
              <LearnArticleCardSkeleton key={i} />
            ))}
          </div>
        ) : (
          <div className="learn-articles-grid mt-10">
            {articles.map((article, index) => (
              <RevealOnScroll key={article.slug} delayMs={index * 80}>
                <LearnArticleCard
                  article={article}
                  expanded={expandedSlug === article.slug}
                  onToggle={() =>
                    setExpandedSlug((current) => (current === article.slug ? null : article.slug))
                  }
                />
              </RevealOnScroll>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
